import { useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Download } from 'lucide-react';
import { cn } from '@/lib/utils';

function extractTableCsv(tableEl: HTMLTableElement): string {
  const rows: string[][] = [];
  for (const tr of Array.from(tableEl.querySelectorAll('tr'))) {
    const cells = Array.from(tr.querySelectorAll('th, td')).map((c) => {
      const text = (c as HTMLElement).innerText.replace(/"/g, '""');
      return `"${text}"`;
    });
    if (cells.length) rows.push(cells);
  }
  return rows.map((r) => r.join(',')).join('\n');
}

function downloadCsv(csv: string, filename = 'table.csv') {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface AssistantMarkdownProps {
  content: string;
  className?: string;
}

export function AssistantMarkdown({ content, className }: AssistantMarkdownProps) {
  return (
    <div className={cn('text-sm', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="text-lg font-semibold mt-4 mb-2 text-foreground">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-base font-semibold mt-3 mb-1.5 text-foreground">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-semibold mt-2 mb-1 text-foreground/90">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-sm font-medium mt-2 mb-1 text-foreground/90">{children}</h4>
          ),
          p: ({ children }) => (
            <p className="leading-relaxed mb-2 last:mb-0">{children}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-0.5 mb-2 ml-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside space-y-0.5 mb-2 ml-1">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:text-primary/80"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-3 border-green-400/60 dark:border-green-500/40 pl-3 text-muted-foreground italic my-2 bg-green-50/30 dark:bg-green-950/10 py-1 rounded-r">
              {children}
            </blockquote>
          ),
          table: ({ children }) => {
            const tableRef = useRef<HTMLTableElement>(null);
            const handleDownload = useCallback(() => {
              if (tableRef.current) {
                downloadCsv(extractTableCsv(tableRef.current));
              }
            }, []);
            return (
              <div className="overflow-x-auto my-2 rounded-md border border-border relative group">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="absolute top-1.5 right-1.5 p-1 rounded bg-muted/80 hover:bg-muted text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  title="Download as CSV"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
                <table ref={tableRef} className="w-full text-xs">{children}</table>
              </div>
            );
          },
          thead: ({ children }) => (
            <thead className="bg-muted/70 border-b-2 border-border">{children}</thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr className="border-b border-border last:border-0 even:bg-muted/20">{children}</tr>
          ),
          th: ({ children }) => (
            <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">{children}</th>
          ),
          td: ({ children }) => <td className="px-2 py-1.5">{children}</td>,
          code({ className: codeClassName, children, ...props }) {
            const match = /language-(\w+)/.exec(codeClassName || '');
            const codeString = String(children).replace(/\n$/, '');
            return match ? (
              <div className="my-2 rounded-lg overflow-hidden">
                <SyntaxHighlighter
                  language={match[1]}
                  style={oneDark}
                  customStyle={{
                    margin: 0,
                    padding: '0.75rem',
                    fontSize: '0.7rem',
                    borderRadius: '0.5rem',
                  }}
                >
                  {codeString}
                </SyntaxHighlighter>
              </div>
            ) : (
              <code
                className="bg-muted/80 text-emerald-700 dark:text-emerald-300 rounded px-1.5 py-0.5 text-[0.8em] font-mono"
                {...props}
              >
                {children}
              </code>
            );
          },
          hr: () => <hr className="my-3 border-border" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
